import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { firstRunCopy } from '@/copy'
import { Button } from '@/ui/Button/Button'
import { TextField } from '@/ui/Field/Field'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { workspacesApi } from '../api/workspaces'
import type { Crumb } from '../components/WsPageShell'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { useGoBack } from '../hooks/useGoBack'
import { useWorkspaceStore } from '../store/workspace'
import styles from './NewWorkspacePage.module.css'

export function NewWorkspacePage() {
  const navigate = useNavigate()
  const goBack = useGoBack()
  const queryClient = useQueryClient()
  const { setActiveWorkspace } = useWorkspaceStore()
  const [name, setName] = useState('')
  const activeName = useWorkspaceStore(
    (s) =>
      s.workspaces.find((w) => w.workspace_id === s.activeWorkspaceId)?.name,
  )

  const createMutation = useMutation({
    mutationFn: (name: string) => workspacesApi.create(name),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      setActiveWorkspace(created.workspace_id)
      void navigate(ROUTES.home)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (name.trim()) createMutation.mutate(name.trim())
  }

  // Without a workspace this is a root-level page: its title says where you are.
  const crumbs: Crumb[] = activeName
    ? [
        { label: activeName, to: ROUTES.home },
        { label: firstRunCopy.newWorkspace.title },
      ]
    : []

  return (
    <WsPageShell
      crumbs={crumbs}
      title={firstRunCopy.newWorkspace.title}
      description={firstRunCopy.newWorkspace.description}
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <Stack gap="space-16">
          <TextField
            id="workspace-name"
            label={firstRunCopy.newWorkspace.name}
            value={name}
            onValue={setName}
            inputProps={{ autoFocus: true }}
            error={
              createMutation.isError
                ? createMutation.error instanceof Error &&
                  createMutation.error.message
                  ? createMutation.error.message
                  : firstRunCopy.newWorkspace.createFailed
                : undefined
            }
          />
          <Inline gap="space-8">
            <Button
              type="submit"
              variant="primary"
              disabled={!name.trim()}
              loading={createMutation.isPending}
            >
              {firstRunCopy.newWorkspace.submit}
            </Button>
            <Button variant="outline" onClick={goBack}>
              {firstRunCopy.newWorkspace.cancel}
            </Button>
          </Inline>
        </Stack>
      </form>
    </WsPageShell>
  )
}
